import { useEffect } from "react";
import { useStoreWithEqualityFn } from "zustand/traditional";

import type { CampaignSchema } from "@/types";
import { useAppStore } from "@/stores";
import {
  campaignKey,
  collectCampaignEntries,
  collectInWindowFinishedEntries,
  matchesKey,
  type OwnedEntry,
} from "@/campaigns";

export type SwitchableCampaign = {
  campaign: CampaignSchema;
  /** The entry with its owning module — what `campaignHook` reads. */
  owned: OwnedEntry;
};

/** Switcher rows are keyed only on `.campaign`; compare by reference so consumers don't
 *  re-render when unrelated store slices change. */
const sameCampaignList = (
  a: SwitchableCampaign[],
  b: SwitchableCampaign[]
): boolean =>
  a.length === b.length && a.every((x, i) => x.campaign === b[i].campaign);

/**
 * The in-game campaign switcher's model, shared by its two surfaces (desktop pill dropdown,
 * mobile dock): which campaigns can be switched to, which is selected, and how to select.
 *
 * Switchable = every type, active first then finished — finished ones narrowed to those still
 * inside their display window: the stream also carries older finished campaigns, but those belong
 * only to the open widget's results grid. Driven by the registry; only `.campaign` is read, so the
 * concrete type never matters here.
 */
export const useSwitchableCampaigns = () => {
  const selectedCampaignId = useAppStore((s) => s.selectedCampaignId);
  const setSelectedCampaignId = useAppStore((s) => s.setSelectedCampaignId);

  const campaigns = useStoreWithEqualityFn(
    useAppStore,
    (s) =>
      [
        ...collectCampaignEntries(s, "active"),
        ...collectInWindowFinishedEntries(s),
      ].map((owned) => ({ campaign: owned.entry.campaign, owned })),
    sameCampaignList
  );

  // A selection can drop out of the switcher while the widget is open — a finished campaign
  // leaving its display window. Re-point it at the first switchable campaign, otherwise the
  // switcher falls back to that one while the view underneath keeps rendering the (still
  // store-resolvable) selected campaign.
  useEffect(() => {
    if (!selectedCampaignId || campaigns.length === 0) return;
    const stillSwitchable = campaigns.some((c) =>
      matchesKey(c.campaign, selectedCampaignId)
    );
    if (stillSwitchable) return;
    setSelectedCampaignId(campaignKey(campaigns[0].campaign));
  }, [selectedCampaignId, campaigns, setSelectedCampaignId]);

  const selected =
    campaigns.find((c) => matchesKey(c.campaign, selectedCampaignId ?? "")) ??
    campaigns[0];
  const others = selected
    ? campaigns.filter((c) => c.campaign.id !== selected.campaign.id)
    : [];

  return {
    campaigns,
    selected,
    others,
    hasMultiple: campaigns.length > 1,
    select: setSelectedCampaignId,
  };
};

/**
 * Just "is there anything to switch to?" — for layout decisions (whether to render the mobile
 * dock and reserve room for it). Deliberately NOT `useSwitchableCampaigns`: that hook also
 * re-points the selection, which must only run where the switcher itself is mounted.
 */
export const useHasMultipleSwitchableCampaigns = (): boolean =>
  useAppStore(
    (s) =>
      collectCampaignEntries(s, "active").length +
        collectInWindowFinishedEntries(s).length >
      1
  );
